<template>
<div>
    <CCol sm="12">
        <CCard>
            <CCardBody class="d-flex flex-column">
                <div class="d-flex mb-2 justify-content-end align-items-start">
                    <!-- Title -->
                    <span class="flex-grow-1" v-if="name"><b>{{ $t('memberIntro') }}</b> {{name}}</span>
                    <!-- Language dropdown -->
                    <LanguageDropdown v-model="currentLanguage"></LanguageDropdown>
                </div>
                <div class="flex-column-responsive gap-1">
                    <!-- Photo -->
                    <div role="group" class="form-group p-3">
                        <GalleryPicker class="gallery-picker" v-model="member.photo" :ratioWidth="1" :ratioHeight="1" v-bind:class="{'is-invalid': genericValidator(member.photo) === false || errorValidator('Photo') === false }" :max="1"></GalleryPicker>
                    </div>
                    <div class="d-flex flex-wrap flex-grow-1">
                        <div role="group" class="form-group col-12">
                            <label class=""> {{ $t('positionOptional') }}</label>
                            <CInput type="number" v-model="member.position" :invalid-feedback="errorOutput['Position']"
                            :is-valid="errorValidator('Position')"></CInput>
                        </div>
                        <!-- Name -->
                        <div role="group" class="form-group col-12">
                            <label class=""> {{ $t('nameMandatory') }}</label>
                            <CInput type="text" v-model="member.name" :invalid-feedback="errorOutput['Name']" :is-valid="genericValidator(member.name) && errorValidator('Name')"></CInput>
                        </div>
                        <!-- Roles -->
                        <div role="group" class="form-group col-12">
                            <label class=""> {{ $t('roleMandatory') }}</label>
                            <CInput type="text" v-model="member.roles[currentLanguage.key]" :invalid-feedback="errorOutput['Roles']" :is-valid="genericValidator(member.roles) && errorValidator('Roles')"></CInput>
                        </div>
                    </div>
                </div>
                <!-- Description -->
                <div role="group" class="form-group col-12">
                    <label class=""> {{ $t('descriptionMandatory') }}</label>
                    <VueEditor v-model="member.descriptions[currentLanguage.key]" v-bind:class="{'is-invalid': genericValidator(member.descriptions) == false || errorValidator('Descriptions') }"></VueEditor>
                    <div v-if="errorOutput['Descriptions']" class="invalid-feedback">{{errorOutput['Descriptions']}}</div>
                </div>
                <!-- Action button + submitting -->  
                <div role="group" class="form-group col-12 d-flex justify-content-end">
                    <CSpinner v-if="submitting" variant="success" small class="align-self-end"></CSpinner>
                    <CButton v-else type="button" class="btn btn-primary" @click="submitClicked">{{ $t('acceptChanges') }}</CButton>
                </div>
                <!-- Loading -->
                <FormLoading v-if="loading"></FormLoading>
            </CCardBody>
        </CCard>
    </CCol>
    <ConfirmationPopUp :confirmationMessage="confirmationMessage" @closeConfirmRequested="onClickConfirmationPopup" v-model="showConfirmationPopup"></ConfirmationPopUp>
</div>
</template>
 
<script>
import { genericValidator, errorValidator, deepClone } from "@/helpers/utils.js";
import Member from "@/classes/Member";
import { mapState, mapActions } from 'vuex'
import LanguageDropdown from "@/controls/LanguageDropdown.vue";
import { CONSTANTS } from '@/helpers/consts';
import ConfirmationPopUp from "@/controls/popups/ConfirmationPopUp.vue";
import GalleryPicker from '@/controls/GalleryPicker.vue'
import { VueEditor } from "vue2-editor";
import CustomPicker from '@/controls/CustomPicker.vue'
import FormLoading from '@/controls/FormLoading.vue'
import { API_HELPER } from "@/helpers/api.js";

  export default {
  components: { LanguageDropdown, ConfirmationPopUp, GalleryPicker, VueEditor, CustomPicker, FormLoading },
    props:{    
        baseMember:
        {
            type: Object,
            default: null,
        },
        baseMemberName:
        {
            type: String,
            default: null,
        },
        loading:
        {
            type: Boolean,
            default: false,
        },
    },
    data()
    {
        return {
            triedSubmit: false,
            currentLanguage: CONSTANTS.supportedLanguages[0],
            member: new Member({}, {}),
            submitting: false,
            showConfirmationPopup: false,
            confirmationMessage:"",
        }
    },
    mounted: function()
    {
        this.resetMember();
    },
    computed:
    {
        ...mapState({ 
            alertType: state => state.alert.type,
            alertOutput: state => state.alert.output
        }),
        errorOutput: function()
        {
            var result = [];

            if(this.alertType == 'alert-danger' && this.alertOutput && Array.isArray(this.alertOutput))
            {
                result = this.alertOutput.reduce(function(accum, currentVal) {
                accum[currentVal.key] = currentVal.value;
                    return accum;
                }, {});
            }

            // Validate multilanguage fields
            for (const [key, value] of Object.entries(this.member.roles)) { if(!value) { result['Roles'] = this.$i18n.t("validateBothTranslations"); break; }; }
            for (const [key, value] of Object.entries(this.member.descriptions)) { if(!value) { result['Descriptions'] = this.$i18n.t("validateBothTranslations"); break; }; }

            return result;
        },
        ...mapState('members', ['postMemberOutput']),
        ...mapState('members', ['putMemberOutput']),
        name: function()
        {
            return this.baseMember || this.baseMemberName ? (this.baseMember ? this.baseMember.name : this.baseMemberName) : null;
        }
    },
    methods:{
        resetMember(){
            if(!this.baseMember)
            {
                var roles = {}; var descriptions = {};
                CONSTANTS.supportedLanguages.forEach(language => { roles[language.key] = ""; descriptions[language.key] = ""; });

                this.member = new Member(roles, descriptions);
            }
            else
            {
                this.member = deepClone(new Member, this.baseMember);          
                this.member.photo = {url: API_HELPER.pathJoin([process.env.VUE_APP_URL_ROOT, this.member.photoPath])};    
            }
        },
        submitClicked(){
            this.triedSubmit = true;
            // Emit event
            if(this.member.isValid())
            {
                this.submitting = true;
                this.$emit('submit-requested', this.member);
            }
        },
        genericValidator,
        errorValidator,
        onClickConfirmationPopup(value){
            this.showConfirmationPopup = value;     
            this.$router.push({name: "MembersList" });
        },
    },
    watch:
    {
        postMemberOutput: function(val)
        {
            if(val)
            {
                this.showConfirmationPopup = true;
            }
        },
        putMemberOutput: function(val)
        {
            if(val)
            {
                this.showConfirmationPopup = true;
            }
        },
        baseMember: function()
        {
            this.resetMember();
        },
        alertType: function()
        {
            this.submitting = false;
        },
        alertOutput: function()
        {
            this.submitting = false;

            // Show pop up in case it is a string error
            // In case it is an array, it is shown on the field
            if(typeof this.alertOutput === 'string' || this.alertOutput instanceof String)
            {
                alert(this.alertOutput);
            }
        }
    }
  }
</script> 
 
<style scoped>

    .showDropdown
    {
        display: block !important;
    }

    .input-group-append
    {
      height: fit-content;
    }

    img
    {
        width: 50%;
        height: 50%;
    }

</style> 